(() => {
  const recentKey = 'rolo-recent-cameras-v2';
  const bindCard = (card) => {
    if (card.dataset.cardBound) return;
    card.dataset.cardBound = 'true';
    const photos = [...card.querySelectorAll('[data-card-photo]')];
    let current = 0;
    const display = (index) => {
      if (!photos[index]) return;
      current = index;
      photos.forEach((photo, i) => { photo.hidden = i !== current; });
    };
    const visual = card.querySelector('.product-card__visual');
    visual?.addEventListener('mouseenter', () => {
      if (photos.length > 1 && window.matchMedia('(hover: hover) and (pointer: fine)').matches) display(1);
    });
    visual?.addEventListener('mouseleave', () => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) display(0);
    });
    card.querySelectorAll('[data-card-step]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (photos.length < 2) return;
        display((current + Number(button.dataset.cardStep) + photos.length) % photos.length);
      });
    });
  };
  // Liquid handles the first 250 products. Load any remaining batches before
  // final ordering, so a later available camera cannot sit behind sold-out stock.
  const bindProductOrder = async (section) => {
    if (section.dataset.orderBound) return;
    section.dataset.orderBound = 'true';
    const grid = section.querySelector('[data-order-grid]');
    if (!grid) return;
    const pageParam = grid.dataset.orderPageParam || 'page';
    const firstPage = new URL(location.href);
    firstPage.searchParams.delete(pageParam);
    firstPage.hash = '';
    let next = Number(grid.dataset.orderPage) > 1 ? firstPage.href : grid.dataset.orderNext;
    if (!next) return; // Normal catalogues are already ordered server-side.
    const cards = Number(grid.dataset.orderPage) > 1 ? [] : [...grid.children];
    const visited = new Set();
    try {
      while (next) {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin) throw new Error('Invalid collection origin');
        url.searchParams.set('section_id', section.dataset.productOrder);
        if (visited.has(url.href)) throw new Error('Repeated collection page');
        visited.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const batch = html.querySelector('[data-order-grid]');
        if (!batch) throw new Error('Missing collection grid');
        cards.push(...batch.children);
        next = batch.dataset.orderNext;
      }
      const unique = [...new Map(cards.map((card) => [card.dataset.productId, card])).values()];
      unique.sort((a, b) => Number(a.dataset.productPosition) - Number(b.dataset.productPosition));
      const available = unique.filter((card) => card.dataset.productAvailable === 'true');
      const cameras = available.filter((card) => card.dataset.productCamera === 'true');
      const candidates = cameras.length ? cameras : available;
      const first = candidates.reduce((cheapest, card) => !cheapest || Number(card.dataset.productPrice) < Number(cheapest.dataset.productPrice) ? card : cheapest, null);
      const ordered = [first, ...available.filter((card) => card !== first), ...unique.filter((card) => card.dataset.productAvailable !== 'true')].filter(Boolean);
      // New copies from another page must receive their own photo controls.
      ordered.forEach(bindCard);
      // Preserve the existing nodes (and their listeners) on the first batch.
      grid.replaceChildren(...ordered);
      section.querySelectorAll('[data-order-pagination]').forEach((nav) => { nav.hidden = true; });
      const empty = section.querySelector('[data-order-empty]');
      if (empty) empty.hidden = ordered.length > 0;
    } catch {
      // Keep the server-rendered products and native pagination usable on failure.
      section.dataset.orderBound = '';
    }
  };
  const showPhoto = (gallery, index) => {
    const photos = [...gallery.querySelectorAll('[data-gallery-image]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!photos[index]) return;
    photos.forEach((photo, i) => { photo.hidden = i !== index; });
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
  };
  const bindDiscovery = (discovery) => {
    const tabs = [...discovery.querySelectorAll('[data-discovery-tab]')];
    const panels = [...discovery.querySelectorAll('[data-discovery-panel]')];
    const output = discovery.querySelector('[data-recent-products]');
    const empty = discovery.querySelector('[data-recent-empty]');
    let product;
    let previous = [];
    try {
      product = JSON.parse(discovery.querySelector('[data-recent-product]').textContent);
      const stored = JSON.parse(localStorage.getItem(recentKey) || '[]');
      previous = Array.isArray(stored) ? stored.filter((item) => item && typeof item.handle === 'string' && /^[a-z0-9-]+$/.test(item.handle) && String(item.id) !== String(product.id)).slice(0, 8) : [];
      localStorage.setItem(recentKey, JSON.stringify([{ id: product.id, handle: product.handle }, ...previous].slice(0, 9)));
    } catch { /* Browsing remains usable when storage is unavailable. */ }
    let recentLoaded = false;
    const loadRecent = async () => {
      if (recentLoaded || !product || !output) return;
      recentLoaded = true;
      const root = window.Shopify?.routes?.root || '/';
      const currency = window.Shopify?.currency?.active || product.currency;
      const format = new Intl.NumberFormat(document.documentElement.lang || 'pt-PT', { style: 'currency', currency });
      const records = await Promise.all(previous.slice(0, 8).map(async (item) => {
        try {
          const response = await fetch(root + 'products/' + encodeURIComponent(item.handle) + '.js', { credentials: 'same-origin' });
          if (!response.ok) return null;
          const data = await response.json();
          return data && data.id && data.handle && data.title ? data : null;
        } catch { return null; }
      }));
      for (const record of records.filter(Boolean)) {
        const card = document.createElement('article');
        card.className = 'product-card' + (record.available ? '' : ' product-card--sold-out');
        const href = root + 'products/' + encodeURIComponent(record.handle);
        const media = document.createElement('a');
        media.className = 'product-card__media';
        media.href = href;
        media.setAttribute('aria-label', record.title);
        const visual = document.createElement('div');
        visual.className = 'product-card__visual';
        const sources = record.images?.length ? record.images : [record.featured_image].filter(Boolean);
        for (const [index, source] of sources.entries()) {
          const url = new URL(typeof source === 'string' ? source : source.src, location.origin);
          if (url.protocol === 'https:' || url.origin === location.origin) {
            const photo = document.createElement('span');
            photo.className = 'product-card__photo';
            photo.dataset.cardPhoto = '';
            photo.hidden = index !== 0;
            const img = document.createElement('img');
            img.className = 'product-card__image';
            img.src = url.href;
            img.alt = record.title;
            img.width = 720;
            img.height = 720;
            img.loading = 'lazy';
            photo.append(img);
            media.append(photo);
          }
        }
        if (!record.available) {
          const badge = document.createElement('span');
          badge.className = 'product-card__badge';
          badge.textContent = discovery.dataset.soldOut;
          media.append(badge);
        }
        const details = document.createElement('div');
        details.className = 'product-card__details';
        const title = document.createElement('h3');
        const link = document.createElement('a');
        link.href = href;
        link.textContent = record.title;
        title.append(link);
        const price = document.createElement('p');
        price.className = 'product-card__price';
        price.textContent = (record.price_varies ? discovery.dataset.from + ' ' : '') + format.format(record.price / 100);
        details.append(title, price);
        visual.append(media);
        if (sources.length > 1) {
          const controls = document.createElement('div');
          controls.className = 'product-card__controls';
          for (const step of [-1, 1]) {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.cardStep = String(step);
            button.setAttribute('aria-label', (step < 0 ? discovery.dataset.previousPhoto : discovery.dataset.nextPhoto) + ': ' + record.title);
            button.textContent = step < 0 ? '‹' : '›';
            controls.append(button);
          }
          visual.append(controls);
        }
        card.append(visual, details);
        output.append(card);
        bindCard(card);
      }
      if (empty) empty.hidden = output.children.length > 0;
    };
    const activate = (tab) => {
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.discoveryPanel !== tab.dataset.discoveryTab; });
      if (tab.dataset.discoveryTab === 'recent') loadRecent();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(tabs[next]);
        tabs[next].focus();
      });
    });
  };
  const bind = (root = document) => {
    root.querySelectorAll('[data-product-card]').forEach(bindCard);
    root.querySelectorAll('[data-product-order]').forEach(bindProductOrder);
    root.querySelectorAll('[data-back-to-top]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' });
      });
    });
    root.querySelectorAll('[data-instagram-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';
      const modal = gallery.querySelector('[data-instagram-modal]');
      gallery.querySelectorAll('[data-instagram-post]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (!modal || !modal.showModal || event.ctrlKey || event.metaKey) return;
          let post;
          try { post = JSON.parse(link.parentElement.querySelector('[data-instagram-data]').textContent); } catch { return; }
          event.preventDefault();
          const image = modal.querySelector('[data-instagram-image]');
          image.src = post.image;
          image.alt = post.alt || '@rolo35mm';
          const caption = modal.querySelector('[data-instagram-caption]');
          caption.textContent = post.caption || '';
          caption.hidden = !post.caption;
          const likes = modal.querySelector('[data-instagram-likes]');
          likes.textContent = likes.dataset.label.replace('__COUNT__', post.likes || '');
          likes.hidden = !post.likes;
          const date = modal.querySelector('[data-instagram-date]');
          date.hidden = !post.date;
          if (/^\d{4}-\d{2}-\d{2}$/.test(post.date || '')) {
            date.dateTime = post.date;
            date.textContent = new Intl.DateTimeFormat(document.documentElement.lang, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(post.date + 'T12:00:00Z'));
          }
          modal.querySelector('[data-instagram-link]').href = post.url;
          modal.showModal();
        });
      });
      modal?.addEventListener('click', (event) => { if (event.target === modal) modal.close(); });
    });
    root.querySelectorAll('[data-dialog-open]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', (event) => {
        const dialog = document.getElementById(button.dataset.dialogOpen);
        if (dialog && typeof dialog.showModal === 'function') { event.preventDefault(); dialog.showModal(); }
      });
    });
    root.querySelectorAll('[data-dialog-close]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    root.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => showPhoto(gallery, Number(thumb.dataset.galleryThumb)));
      });
    });
    root.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const selected = select.selectedOptions[0];
        const section = select.closest('[data-product-section]');
        if (!selected || !section) return;
        const available = selected.dataset.available === 'true';
        section.querySelectorAll('[data-product-price], [data-sticky-price]').forEach((price) => { price.textContent = selected.dataset.price || ''; });
        section.querySelectorAll('[data-add-to-cart], [data-sticky-add]').forEach((button) => {
          button.disabled = !available;
          button.textContent = available ? button.dataset.addLabel : button.dataset.soldOut;
        });
        const express = section.querySelector('[data-dynamic-checkout]');
        if (express) express.hidden = !available;
        const quantity = select.form?.querySelector('input[name="quantity"]');
        if (quantity) quantity.value = selected.dataset.min || '1';
        const gallery = section.querySelector('[data-gallery]');
        if (gallery && selected.dataset.imageId) {
          const index = [...gallery.querySelectorAll('[data-gallery-image]')].findIndex((photo) => photo.dataset.galleryImage === selected.dataset.imageId);
          if (index >= 0) showPhoto(gallery, index);
        }
        const url = new URL(location.href);
        url.searchParams.set('variant', selected.value);
        history.replaceState(null, '', url);
        updateScroll();
      });
    });
    root.querySelectorAll('[data-product-discovery]').forEach((discovery) => {
      if (discovery.dataset.bound) return;
      discovery.dataset.bound = 'true';
      bindDiscovery(discovery);
    });
    root.querySelectorAll('.nav-dropdown, .mobile-menu, .language-picker').forEach((menu) => {
      if (menu.dataset.bound) return;
      menu.dataset.bound = 'true';
      menu.addEventListener('toggle', () => {
        if (menu.open) menu.closest('[data-rolo-header]')?.querySelectorAll('details[open]').forEach((other) => {
          if (other !== menu && !other.contains(menu) && !menu.contains(other)) other.open = false;
        });
        document.body.classList.toggle('mobile-menu-open', !!document.querySelector('.mobile-menu[open]'));
        updateScroll();
      });
    });
  };
  const bindFaq = (root = document) => {
    root.querySelectorAll('[data-rolo-faq]').forEach((content) => {
      if (content.dataset.faqBound) return;
      content.dataset.faqBound = 'true';
      const nodes = [...content.children];
      const fragment = document.createDocumentFragment();
      let group;
      let answer;
      let groupCount = 0;
      nodes.forEach((node) => {
        if (node.matches('h2')) {
          group = document.createElement('section');
          group.className = 'rolo-faq__group';
          group.dataset.faqGroup = String(++groupCount);
          node.className = 'rolo-faq__heading';
          node.id = `rolo-faq-group-${groupCount}`;
          group.setAttribute('aria-labelledby', node.id);
          group.append(node);
          fragment.append(group);
          answer = null;
        } else if (node.matches('h3') && group) {
          const question = document.createElement('details');
          question.className = 'rolo-faq__question';
          question.name = `rolo-faq-${groupCount}`;
          const summary = document.createElement('summary');
          const title = document.createElement('span');
          title.textContent = node.textContent;
          summary.append(title);
          const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          chevron.setAttribute('viewBox', '0 0 24 24');
          chevron.setAttribute('aria-hidden', 'true');
          chevron.innerHTML = '<path d="m5 9 7 7 7-7" fill="none" stroke="currentColor" stroke-width="1.5"/>';
          summary.append(chevron);
          answer = document.createElement('div');
          answer.className = 'rolo-faq__answer rte';
          question.append(summary, answer);
          group.append(question);
        } else if (answer && !node.matches('hr')) {
          answer.append(node);
        } else if (!group && !node.matches('hr')) {
          fragment.append(node);
        }
      });
      if (groupCount) content.replaceChildren(fragment);
    });
  };
  let previousY = Math.max(window.scrollY, 0);
  let direction = 'up';
  const updateScroll = () => {
    const y = Math.max(window.scrollY, 0);
    const delta = y - previousY;
    if (Math.abs(delta) > 5) { direction = delta > 0 ? 'down' : 'up'; previousY = y; }
    const header = document.querySelector('[data-rolo-header]');
    const menuOpen = !!header?.querySelector('details[open]');
    header?.classList.toggle('is-scrolled', y > 40);
    header?.closest('[id^="shopify-section-"]')?.classList.toggle('rolo-header-shell--hidden', y > 160 && direction === 'down' && !menuOpen);
    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const bar = section.querySelector('[data-sticky-product-bar]');
      const add = section.querySelector('[data-add-to-cart]');
      if (bar && add) bar.hidden = add.disabled || direction !== 'down' || add.getBoundingClientRect().bottom > 0;
    });
    document.querySelectorAll('[data-back-to-top]').forEach((button) => {
      button.hidden = y < 500;
      button.classList.toggle('back-to-top--raised', !!document.querySelector('[data-sticky-product-bar]:not([hidden])'));
    });
  };
  let scheduled = false;
  window.addEventListener('scroll', () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; updateScroll(); });
  }, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.mobile-menu[open], .nav-dropdown[open], .language-picker[open]').forEach((menu) => {
      menu.open = false;
      if (menu.contains(document.activeElement)) menu.querySelector('summary')?.focus();
    });
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-rolo-header]')) document.querySelectorAll('.nav-dropdown[open], .language-picker[open]').forEach((menu) => { menu.open = false; });
  });
  document.addEventListener('shopify:section:load', (event) => { bind(event.target); bindFaq(event.target); updateScroll(); });
  bind();
  bindFaq();
  updateScroll();
})();
